import React, { useState, useCallback } from 'react';
import { Button, Popconfirm, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { FM211, FM212 } from '../../../services/m207Api';
import LookupField from '../../../components/LookupField';

interface Tab2Props {
  details: Partial<FM211>[];
  isEditing: boolean;
  onAdd: () => void;
  onDelete: (idx: number) => void;
  onDetailChange: (idx: number, key: keyof FM211, value: FM211[keyof FM211]) => void;
  onBoardNumbersChange: (detailIdx: number, boardNumbers: Partial<FM212>[]) => void;
  totalCount: number;
}

const Tab2Details: React.FC<Tab2Props> = ({
  details, isEditing, onAdd, onDelete, onDetailChange, onBoardNumbersChange, totalCount,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [activeDetailIdx, setActiveDetailIdx] = useState(-1);
  const [editingBoardNumbers, setEditingBoardNumbers] = useState<Partial<FM212>[]>([]);

  /** Open the board numbers modal for a detail row */
  const openBoardModal = useCallback((idx: number) => {
    setActiveDetailIdx(idx);
    const bns = details[idx]?.board_numbers || [];
    setEditingBoardNumbers(bns.map(bn => ({ ...bn })));
    setModalVisible(true);
  }, [details]);

  /** Save board numbers back to parent */
  const saveBoardNumbers = useCallback(() => {
    if (activeDetailIdx >= 0) {
      onBoardNumbersChange(activeDetailIdx, editingBoardNumbers);
    }
    setModalVisible(false);
  }, [activeDetailIdx, editingBoardNumbers, onBoardNumbersChange]);

  /** Add a board number row */
  const addBoardRow = useCallback(() => {
    setEditingBoardNumbers(prev => [
      ...prev,
      { fm212_03: prev.length + 1, fm212_11: '', fm212_12: '' },
    ]);
  }, []);

  /** Delete a board number row */
  const deleteBoardRow = useCallback((idx: number) => {
    setEditingBoardNumbers(prev => prev.filter((_, i) => i !== idx));
  }, []);

  /** Update a board number field */
  const updateBoardField = useCallback((idx: number, key: keyof FM212, value: string) => {
    setEditingBoardNumbers(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, []);

  return (
    <div className="m207-tab2">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>修改明細 ({details.length} 筆)</span>
        {isEditing && (
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={onAdd}>
            新增明細
          </Button>
        )}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="detail-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th style={{ width: 160 }}>產品代號</th>
              <th style={{ width: 140 }}>批號</th>
              <th style={{ width: 80 }}>色號</th>
              <th style={{ width: 70 }}>板號數量</th>
              <th style={{ width: 80 }}>板號對照</th>
              {isEditing && <th style={{ width: 50 }}>操作</th>}
            </tr>
          </thead>
          <tbody>
            {details.length === 0 && (
              <tr>
                <td colSpan={isEditing ? 7 : 6} style={{ textAlign: 'center', color: '#999', padding: 24 }}>
                  尚無明細資料
                </td>
              </tr>
            )}
            {details.map((d, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <LookupField
                      lookupType="product"
                      maxLength={6}
                      disabled={!isEditing}
                      value={d.fm211_121 || ''}
                      onChange={v => onDetailChange(idx, 'fm211_121', v)}
                      onCodeConfirm={v => onDetailChange(idx, 'fm211_121', v)}
                      style={{ flex: 1 }}
                    />
                    <input
                      type="text" maxLength={6}
                      disabled={!isEditing}
                      value={d.fm211_122 || ''}
                      onChange={e => onDetailChange(idx, 'fm211_122', e.target.value)}
                      style={{ width: 60 }}
                    />
                  </div>
                </td>
                <td>
                  <LookupField
                    lookupType="batch"
                    maxLength={12}
                    disabled={!isEditing}
                    value={d.fm211_11 || ''}
                    onChange={v => onDetailChange(idx, 'fm211_11', v)}
                    onCodeConfirm={v => onDetailChange(idx, 'fm211_11', v)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    disabled
                    value={d.fm211_14 || ''}
                    style={{ width: '100%' }}
                  />
                </td>
                <td style={{ textAlign: 'center' }}>
                  {d.board_numbers?.length || d.fm211_13 || 0}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openBoardModal(idx)}
                  >
                    {isEditing ? '編輯' : '檢視'}
                  </Button>
                </td>
                {isEditing && (
                  <td>
                    <Popconfirm title="確定刪除此明細?" onConfirm={() => onDelete(idx)}>
                      <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Context strip — below grid, matching FoxPro layout */}
      <div className="context-strip">
        <div className="context-left">
          <span className="context-label">色號 :</span>
          <span className="context-value">{/* Color card info — populated on row selection */}</span>
        </div>
        <div className="context-right">
          <span className="context-label">總項數</span>
          <span className="context-count">{totalCount}</span>
        </div>
      </div>

      {/* Board Number Modal (FM212) */}
      <Modal
        title={`板號對照 — 明細第 ${activeDetailIdx + 1} 行`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={isEditing ? saveBoardNumbers : () => setModalVisible(false)}
        okText={isEditing ? '確定' : '關閉'}
        cancelButtonProps={{ style: isEditing ? {} : { display: 'none' } }}
        width={600}
      >
        {isEditing && (
          <div style={{ marginBottom: 8, textAlign: 'right' }}>
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={addBoardRow}>
              新增板號
            </Button>
          </div>
        )}
        <table className="detail-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>舊板號</th>
              <th>新板號</th>
              {isEditing && <th style={{ width: 50 }}>操作</th>}
            </tr>
          </thead>
          <tbody>
            {editingBoardNumbers.length === 0 && (
              <tr>
                <td colSpan={isEditing ? 4 : 3} style={{ textAlign: 'center', color: '#999', padding: 16 }}>
                  尚無板號對照
                </td>
              </tr>
            )}
            {editingBoardNumbers.map((bn, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>
                  <input
                    type="text" maxLength={20}
                    disabled={!isEditing}
                    value={bn.fm212_11 || ''}
                    onChange={e => updateBoardField(idx, 'fm212_11', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                <td>
                  <input
                    type="text" maxLength={20}
                    disabled={!isEditing}
                    value={bn.fm212_12 || ''}
                    onChange={e => updateBoardField(idx, 'fm212_12', e.target.value)}
                    style={{ width: '100%' }}
                  />
                </td>
                {isEditing && (
                  <td>
                    <Popconfirm title="刪除此板號?" onConfirm={() => deleteBoardRow(idx)}>
                      <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    </div>
  );
};

export default Tab2Details;
